import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpring, animated } from '@react-spring/web';
import api from '../api/axios';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import CustomAlert from './CustomAlert';
import { useCustomAlert } from '../hooks/useCustomAlert';

export default function BookingForm({ booking, onSave, onCancel }) {
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    user_id: '',
    property_id: '',
    start_date: '',
    end_date: '',
    status: 'pending',
    total_amount: 0,
    guests: 1
  });
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { alertConfig, showAlert, hideAlert } = useCustomAlert();
  
  const progressSpring = useSpring({
    width: loading ? '100%' : '0%',
    config: { tension: 200, friction: 25 }
  });

  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end - start;
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const calculateTotal = (startDate, endDate, pricePerNight) => {
    if (!startDate || !endDate || !pricePerNight) return;
    const days = calculateDays(startDate, endDate);
    const total = days * (pricePerNight || 0);
    setFormData(prev => ({...prev, total_amount: total}));
  };

  const validateDates = (startDate, endDate) => {
    if (!startDate || !endDate) return true;
    return new Date(endDate) > new Date(startDate);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, propertiesRes] = await Promise.all([
          api.get('/users'),
          api.get('/properties')
        ]);
        
        const fetchedProperties = propertiesRes.data?.data || propertiesRes.data || [];
        setUsers(usersRes.data?.data || usersRes.data || []);
        setProperties(fetchedProperties);
      } catch (error) {
        console.error('Error fetching data:', error);
        setUsers([]);
        setProperties([]);
      }
    };
    
    fetchData();
  }, []);
  
  useEffect(() => {
    if (booking && properties.length > 0) {
      console.log('Setting form data from booking:', booking);
      console.log('Available properties:', properties.length);
      
      setFormData({
        user_id: String(booking.user_id || ''),
        property_id: String(booking.property_id || ''),
        start_date: booking.start_date || '',
        end_date: booking.end_date || '',
        status: booking.status || 'pending',
        total_amount: booking.total_amount || 0,
        guests: booking.guests || 1
      });
      
      const property = properties.find(p => p.id == booking.property_id);
      console.log('Found property:', property);
      if (property) {
        setSelectedProperty(property);
      }
    } else if (!booking) {
      // Reset form when no booking (add mode)
      setFormData({
        user_id: '',
        property_id: '',
        start_date: '',
        end_date: '',
        status: 'pending',
        total_amount: 0,
        guests: 1
      });
      setSelectedProperty(null);
    }
  }, [booking, properties]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Validate dates before submission
    if (!validateDates(formData.start_date, formData.end_date)) {
      showAlert({
        title: 'Tanggal Tidak Valid',
        message: 'Tanggal selesai harus setelah tanggal mulai!',
        type: 'warning'
      });
      setLoading(false);
      return;
    }
    
    // Validate required fields
    if (!formData.user_id || !formData.property_id || !formData.start_date || !formData.end_date) {
      showAlert({
        title: 'Data Tidak Lengkap',
        message: 'Mohon lengkapi semua field yang wajib diisi!',
        type: 'warning'
      });
      setLoading(false);
      return;
    }
    
    try {
      if (booking) {
        const bookingData = {
          user_id: parseInt(formData.user_id),
          property_id: parseInt(formData.property_id),
          start_date: formData.start_date,
          end_date: formData.end_date,
          status: formData.status || 'pending',
          total_amount: parseFloat(formData.total_amount) || 0,
          guests: parseInt(formData.guests) || 1
        };
        await api.put(`/bookings/${booking.id}`, bookingData);
      } else {
        // Ensure numeric values before sending
        const bookingData = {
          user_id: parseInt(formData.user_id),
          property_id: parseInt(formData.property_id),
          start_date: formData.start_date,
          end_date: formData.end_date,
          status: formData.status || 'pending',
          total_amount: parseFloat(formData.total_amount) || 0,
          guests: parseInt(formData.guests) || 1
        };
        
        console.log('Sending booking data:', bookingData);
        await api.post('/bookings', bookingData);
      }
      onSave();
    } catch (error) {
      console.error('Booking error:', error.response?.data);
      const errorMsg = error.response?.data?.message || error.response?.data?.errors || error.message || 'Failed to save booking';
      
      // Format error message if it's an object
      let finalMessage = '';
      if (typeof errorMsg === 'object') {
        finalMessage = Object.values(errorMsg).flat().join(', ');
      } else {
        finalMessage = errorMsg;
      }
      
      // Show custom alert popup
      showAlert({
        title: 'Booking Gagal',
        message: finalMessage,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomAlert
        show={alertConfig.show}
        onClose={hideAlert}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        confirmText={alertConfig.confirmText}
        cancelText={alertConfig.cancelText}
        showCancel={alertConfig.showCancel}
        onConfirm={alertConfig.onConfirm}
      />
      
      <AnimatePresence>
        <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            onCancel();
          }
        }}
      >
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-2xl border w-full max-w-2xl max-h-[90vh] overflow-y-auto mt-20`}
        >
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`sticky top-0 ${isDark ? 'bg-gray-800/95 border-gray-700' : 'bg-white/95 border-gray-200'} backdrop-blur-xl border-b p-6 rounded-t-2xl`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <motion.div 
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", damping: 15, delay: 0.2 }}
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 shadow-lg ${isDark ? 'bg-gradient-to-br from-gray-600 to-gray-700' : 'bg-gradient-to-br from-blue-500 to-blue-600'}`}
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h2 className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent'}`}>{booking ? t('edit_booking') : t('add_booking')}</h2>
                  <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm`}>{t('manage_booking_info')}</p>
                </motion.div>
              </div>
              <motion.button 
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={onCancel}
                className={`p-2 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} rounded-lg transition-colors`}
              >
                <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </motion.button>
            </div>
          </motion.div>

          {/* Form Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6"
          >
            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl"
                >
                  <div className="flex items-center">
                    <motion.svg 
                      initial={{ rotate: -90 }}
                      animate={{ rotate: 0 }}
                      className="w-5 h-5 mr-2" 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </motion.svg>
                    {error}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.form 
              onSubmit={handleSubmit} 
              className="space-y-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, staggerChildren: 0.1 }}
            >
              {/* Progress Bar */}
              {loading && (
                <div className="w-full bg-gray-200 rounded-full h-1 mb-4">
                  <animated.div 
                    style={progressSpring}
                    className={`${'bg-gradient-to-r from-blue-500 to-blue-600'} h-1 rounded-full`}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{t('user')} *</label>
                  <select
                    value={formData.user_id}
                    onChange={(e) => setFormData({...formData, user_id: e.target.value})}
                    className={`w-full p-3 border border-gray-300 rounded-xl ${isDark ? 'bg-gray-700/50 text-gray-100 border-gray-600' : 'bg-white/50'} focus:ring-2 ${'focus:ring-blue-500 focus:border-blue-500'} transition-all hover:shadow-md`}
                    required
                  >
                    <option value="">{t('select_user')}</option>
                    {Array.isArray(users) && users.map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{t('property')} *</label>
                  <select
                    value={formData.property_id}
                    onChange={(e) => {
                      const propertyId = e.target.value;
                      const property = properties.find(p => p.id == propertyId);
                      setSelectedProperty(property);
                      setFormData({...formData, property_id: propertyId});
                      calculateTotal(formData.start_date, formData.end_date, property?.price || 0);
                    }}
                    className={`w-full p-3 border border-gray-300 rounded-xl ${isDark ? 'bg-gray-700/50 text-gray-100 border-gray-600' : 'bg-white/50'} focus:ring-2 ${'focus:ring-blue-500 focus:border-blue-500'} transition-all hover:shadow-md`}
                    required
                  >
                    <option value="">{t('select_property')}</option>
                    {Array.isArray(properties) && properties.map(property => (
                      <option key={property.id} value={property.id}>{property.name} - Rp {Number(property.price || 0).toLocaleString()}/night</option>
                    ))}
                  </select>
                  {selectedProperty && (
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-1`}>Price: Rp {Number(selectedProperty.price || 0).toLocaleString()}/night</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{t('start_date')} *</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => {
                      const startDate = e.target.value;
                      setFormData({...formData, start_date: startDate});
                      if (formData.end_date && !validateDates(startDate, formData.end_date)) {
                        setError(t('end_date_after_start'));
                        return;
                      }
                      setError('');
                      calculateTotal(startDate, formData.end_date, selectedProperty?.price || 0);
                    }}
                    className={`w-full p-3 border border-gray-300 rounded-xl ${isDark ? 'bg-gray-700/50 text-gray-100 border-gray-600' : 'bg-white/50'} focus:ring-2 ${'focus:ring-blue-500 focus:border-blue-500'} transition-all hover:shadow-md`}
                    required
                  />
                </div>
                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{t('end_date')} *</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => {
                      const endDate = e.target.value;
                      if (formData.start_date && !validateDates(formData.start_date, endDate)) {
                        setError(t('end_date_after_start'));
                        return;
                      }
                      setError('');
                      setFormData({...formData, end_date: endDate});
                      calculateTotal(formData.start_date, endDate, selectedProperty?.price || 0);
                    }}
                    className={`w-full p-3 border border-gray-300 rounded-xl ${isDark ? 'bg-gray-700/50 text-gray-100 border-gray-600' : 'bg-white/50'} focus:ring-2 ${'focus:ring-blue-500 focus:border-blue-500'} transition-all hover:shadow-md`}
                    min={formData.start_date}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{t('status')}</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className={`w-full p-3 border border-gray-300 rounded-xl ${isDark ? 'bg-gray-700/50 text-gray-100 border-gray-600' : 'bg-white/50'} focus:ring-2 ${'focus:ring-blue-500 focus:border-blue-500'} transition-all hover:shadow-md`}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{t('guests')}</label>
                  <input
                    type="number"
                    value={formData.guests}
                    onChange={(e) => setFormData({...formData, guests: e.target.value})}
                    min="1"
                    className={`w-full p-3 border border-gray-300 rounded-xl ${isDark ? 'bg-gray-700/50 text-gray-100 border-gray-600' : 'bg-white/50'} focus:ring-2 ${'focus:ring-blue-500 focus:border-blue-500'} transition-all hover:shadow-md`}
                  />
                </div>
              </div>
              
              <div>
                <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{t('total_cost')}</label>
                <input
                  type="number"
                  value={formData.total_amount}
                  onChange={(e) => setFormData({...formData, total_amount: e.target.value})}
                  className={`w-full p-3 border border-gray-300 rounded-xl ${isDark ? 'bg-gray-600/50 text-gray-100 border-gray-600' : 'bg-gray-50'} focus:ring-2 ${'focus:ring-blue-500 focus:border-blue-500'} transition-all`}
                  readOnly
                />
                {formData.start_date && formData.end_date && selectedProperty && (
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                    {calculateDays(formData.start_date, formData.end_date)} {t('nights')} × Rp {Number(selectedProperty.price || 0).toLocaleString()}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={`flex flex-col sm:flex-row gap-3 pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              >
                <motion.button 
                  type="submit" 
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex-1 text-white px-6 py-3 rounded-xl font-semibold focus:ring-4 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 focus:ring-gray-500' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:ring-blue-200'}`}
                >
                  {loading ? (
                    <>
                      <motion.div 
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"
                      />
                      {t('saving')}
                    </>
                  ) : (
                    <>
                      <motion.svg 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.5 }}
                        className="w-5 h-5 mr-2" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </motion.svg>
                      {booking ? t('update_booking') : t('create_booking')}
                    </>
                  )}
                </motion.button>
                <motion.button 
                  type="button" 
                  onClick={onCancel}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`px-6 py-3 border ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} rounded-xl font-semibold focus:ring-4 focus:ring-gray-200 transition-all duration-200 flex items-center justify-center`}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {t('cancel')}
                </motion.button>
              </motion.div>
            </motion.form>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
    </>
  );
}